import {
  GlobalSearchHistoryItemType,
  GlobalSearchItem,
} from "@redotech/merchant-sdk/rpc/schema/global-search/global-search-schema";
import { t } from "@redotech/redo-i18n";
import { MerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import { getOrderNavigationUrl } from "@redotech/redo-merchant-fulfillment-app/common/order-navigation-url";
import { getReturnHref } from "@redotech/redo-return-core-common/merchant-record-url";
import { returnTypeToTitle } from "@redotech/redo-return-core-common/return";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { CommandPaletteItem } from "@redotech/redo-web/command-palette/contexts/command-palette-context";
import { filterTruthy } from "@redotech/util/array";
import { isEnumValue } from "@redotech/util/enum";
import { NavigateFunction } from "react-router-dom";
import { getReturnListStatusLabel } from "../../../../return/returns-list-labels";
import { shipmentStatusName } from "../../../../return/util";
import { getWithdrawalStatusLabel } from "../../../../return/withdrawals/withdrawals-list-labels";

export type GlobalSearchItemRoutes = Pick<
  MerchantRoutes,
  | "shippingBaseUrl"
  | "ordersOutboundLabelsHref"
  | "returnsBaseUrl"
  | "getCustomerRecordHref"
  | "getConversationHref"
>;

type ReturnSearchItem = Extract<
  GlobalSearchItem,
  { type: GlobalSearchHistoryItemType.RETURN }
>;

/**
 * Search history saved before the server sent the raw type holds a title such
 * as "Shipping claim" instead; those still open as a return.
 */
export function returnTypeOfSearchItem(item: ReturnSearchItem): ReturnTypeEnum {
  return isEnumValue(item.returnType, ReturnTypeEnum)
    ? item.returnType
    : ReturnTypeEnum.RETURN;
}

/** Only the return records of one type, for a search scoped to that type. */
export function filterGlobalSearchItemsByReturnType(
  items: GlobalSearchItem[],
  returnType: ReturnTypeEnum,
): GlobalSearchItem[] {
  return items.filter(
    (item) =>
      item.type === GlobalSearchHistoryItemType.RETURN &&
      returnTypeOfSearchItem(item) === returnType,
  );
}

function openHref(
  href: string,
  navigate: NavigateFunction,
  { newTab }: { newTab?: boolean },
) {
  if (newTab) {
    window.open(href, "_blank");
    return;
  }
  navigate(href);
}

export function globalSearchItemToCommandPaletteItem(
  result: GlobalSearchItem,
  routes: GlobalSearchItemRoutes,
  navigate: NavigateFunction,
): CommandPaletteItem {
  switch (result.type) {
    case GlobalSearchHistoryItemType.ORDER:
      return {
        id: `order-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.orderCreatedAt.toLocaleDateString(),
          `$${result.price.toFixed(2)}`,
          t("{{count}} items", {
            count: result.items,
            context: "number of items in an order search result",
          }),
          shipmentStatusName(result.fulfillmentStatus),
        ]).join(" | "),
        category: "orders",
        group: t("Orders", {
          context: "navigation category for merchant sales orders",
        }),
        action: (options) =>
          openHref(
            `${routes.shippingBaseUrl}/outbound-labels/orders/${result.id}`,
            navigate,
            options,
          ),
      };

    case GlobalSearchHistoryItemType.SHIPMENT:
      return {
        id: `shipment-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.recipientName,
          result.shipmentCreatedAt.toLocaleDateString(),
          result.trackingCode && result.otherTrackingCodeCount
            ? `${result.trackingCode} +${result.otherTrackingCodeCount}`
            : result.trackingCode,
          shipmentStatusName(result.shipmentStatus),
        ]).join(" | "),
        category: "shipments",
        group: t("Fulfillments", {
          context:
            "search category for merchant orders being picked, packed, and shipped from a warehouse, not abstract completion",
        }),
        action: (options) =>
          openHref(
            getOrderNavigationUrl({
              fulfillmentGroupId: result.id,
              orderId: { id: result.externalOrderId },
              ordersOutboundLabelsHref: routes.ordersOutboundLabelsHref,
            }),
            navigate,
            options,
          ),
      };

    case GlobalSearchHistoryItemType.CUSTOMER:
      return {
        id: `customer-${result.id}`,
        title: result.customerName,
        subtitle: filterTruthy([result.email, result.phone]).join(" | "),
        category: "customers",
        group: t("Customers", {
          context: "navigation category for merchant customer records",
        }),
        action: (options) =>
          openHref(
            routes.getCustomerRecordHref({ customerId: result.id }),
            navigate,
            options,
          ),
      };

    case GlobalSearchHistoryItemType.RETURN: {
      const returnType = returnTypeOfSearchItem(result);
      return {
        id: `return-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.returnStartedAtDate.toLocaleDateString(),
          result.returnValue != null
            ? `$${result.returnValue.toFixed(2)}`
            : null,
          returnTypeToTitle[returnType],
          getReturnListStatusLabel(result.returnStatus),
        ]).join(" | "),
        category: "returns",
        group: t("Returns", {
          context: "navigation category for customer product returns",
        }),
        action: (options) =>
          openHref(
            getReturnHref(routes.returnsBaseUrl, {
              _id: result.id,
              type: returnType,
            }),
            navigate,
            options,
          ),
      };
    }

    case GlobalSearchHistoryItemType.WITHDRAWAL:
      return {
        id: `withdrawal-${result.id}`,
        title: result.orderName,
        subtitle: filterTruthy([
          result.customerName,
          result.createdAt.toLocaleDateString(),
          getWithdrawalStatusLabel(result.status),
        ]).join(" | "),
        category: "withdrawals",
        group: t("Withdrawals", {
          context:
            "search category for customer statutory withdrawal-from-purchase requests, equivalent to the legal right of withdrawal, not cash or inventory withdrawals",
        }),
        action: (options) =>
          openHref(
            getReturnHref(routes.returnsBaseUrl, {
              _id: result.id,
              type: ReturnTypeEnum.WITHDRAWAL,
            }),
            navigate,
            options,
          ),
      };

    case GlobalSearchHistoryItemType.CONVERSATION:
      return {
        id: `conversation-${result.id}`,
        title: result.customer,
        subtitle: filterTruthy([
          result.createdAt.toLocaleDateString(),
          result.subject,
          result.platform,
        ]).join(" | "),
        category: "conversations",
        group: t("Conversations", {
          context: "navigation category for customer support conversations",
        }),
        action: (options) =>
          openHref(routes.getConversationHref(result.id), navigate, options),
      };

    case GlobalSearchHistoryItemType.DOCUMENTATION:
      return {
        id: `article-${result.id}`,
        title: result.title,
        subtitle: result.description || undefined,
        category: "help-articles",
        group: t("Help Articles", {
          context: "navigation category for product help documentation",
        }),
        action: () => {
          window.open(result.url, "_blank");
        },
      };

    case GlobalSearchHistoryItemType.PAGE:
      return {
        id: `page-${result.id}`,
        title:
          result.breadcrumbs[result.breadcrumbs.length - 1] ||
          t("Page", { context: "generic merchant app page title" }),
        subtitle: result.breadcrumbs.slice(0, -1).join(" > ") || undefined,
        category: "pages",
        group: t("Pages", {
          context: "navigation category for merchant app pages",
        }),
        // The page id is its route path.
        action: (options) => openHref(result.id, navigate, options),
      };

    default: {
      const exhaustiveCheck: never = result;
      throw new Error(`Unhandled GlobalSearchItem type: ${exhaustiveCheck}`);
    }
  }
}
